// frontend/src/api/adminMonitoring.js
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8010";

// Public API methods (no authentication required)
export const publicSettingsAPI = {
  // Get Export PDF button status (public endpoint)
  getExportPdfStatus: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/export-pdf-status`);
      return response.data;
    } catch (error) {
      console.error('Error fetching export PDF status:', error);
      // Default to enabled if API fails
      return { enabled: true };
    }
  },
  // Get Shipping File Upload button status (public endpoint)
  getShippingFileUploadStatus: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/shipping-file-upload-status`);
      return response.data;
    } catch (error) {
      console.error('Error fetching shipping file upload status:', error);
      // Default to disabled if API fails (as per user request)
      return { enabled: false };
    }
  },
  // Get Browser Extension page status (public endpoint)
  getBrowserExtensionStatus: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/browser-extension-status`);
      return response.data;
    } catch (error) {
      console.error('Error fetching browser extension status:', error);
      // Default to enabled if API fails
      return { enabled: true };
    }
  },
  // Get Reconciliation Issues page status (public endpoint)
  getReconciliationIssuesStatus: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/reconciliation-issues-status`);
      return response.data;
    } catch (error) {
      console.error('Error fetching reconciliation issues status:', error);
      // Default to enabled if API fails
      return { enabled: true };
    }
  },
  // Get Create AWB page status (public endpoint)
  getAWBCreateStatus: async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/awb-create-status`);
      return response.data;
    } catch (error) {
      console.error('Error fetching AWB create status:', error);
      // Default to enabled if API fails
      return { enabled: true };
    }
  }
};

// Create axios instance with auth headers
const createAuthenticatedRequest = () => {
  const token = localStorage.getItem('authToken');
  return axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
};

const adminMonitoringAPI = {
  // Get API logs
  getApiLogs: async (limit = 100, apiName = null) => {
    const params = { limit };
    if (apiName) params.api_name = apiName;
    
    const api = createAuthenticatedRequest();
    const response = await api.get('/admin/api/logs', { params });
    return response.data;
  },

  // Get API call types and stats
  getApiCallTypes: async () => {
    const api = createAuthenticatedRequest();
    const response = await api.get('/admin/api/call-types');
    return response.data;
  },

  // Get recent API activity
  getRecentActivity: async (hours = 24) => {
    const api = createAuthenticatedRequest();
    const response = await api.get('/admin/api/recent-activity', {
      params: { hours }
    });
    return response.data;
  },

  // Get API statistics
  getApiStats: async () => {
    const api = createAuthenticatedRequest();
    const response = await api.get('/admin/api/stats');
    return response.data;
  },

  // Get system settings
  getSystemSettings: async () => {
    const api = createAuthenticatedRequest();
    const response = await api.get('/admin/system-settings');
    return response.data;
  },

  // Update system setting
  updateSystemSetting: async (settingId, settingData) => {
    const api = createAuthenticatedRequest();
    const response = await api.put(`/admin/system-settings/${settingId}`, settingData);
    return response.data;
  },

  // Get registration status
  getRegistrationStatus: async () => {
    const api = createAuthenticatedRequest();
    const response = await api.get('/admin/system-settings/registration');
    return response.data;
  },

  // Update registration status
  updateRegistrationStatus: async (enabled) => {
    const api = createAuthenticatedRequest();
    const response = await api.put('/admin/system-settings/registration', {
      setting_value: enabled,
      enabled: enabled
    });
    return response.data;
  },

  // Get Export PDF button status
  getExportPdfStatus: async () => {
    const api = createAuthenticatedRequest();
    const response = await api.get('/admin/system-settings/export-pdf');
    return response.data;
  },

  // Update Export PDF button status
  updateExportPdfStatus: async (enabled) => {
    const api = createAuthenticatedRequest();
    const response = await api.put('/admin/system-settings/export-pdf', {
      setting_value: enabled,
      enabled: enabled
    });
    return response.data;
  },

  // Get Shipping File Upload button status
  getShippingFileUploadStatus: async () => {
    const api = createAuthenticatedRequest();
    const response = await api.get('/admin/system-settings/shipping-file-upload');
    return response.data;
  },

  // Update Shipping File Upload button status
  updateShippingFileUploadStatus: async (enabled) => {
    const api = createAuthenticatedRequest();
    const response = await api.put('/admin/system-settings/shipping-file-upload', {
      setting_value: enabled,
      enabled: enabled
    });
    return response.data;
  },

  // Get Browser Extension page status
  getBrowserExtensionStatus: async () => {
    const api = createAuthenticatedRequest();
    const response = await api.get('/admin/system-settings/browser-extension');
    return response.data;
  },

  // Update Browser Extension page status
  updateBrowserExtensionStatus: async (enabled) => {
    const api = createAuthenticatedRequest();
    const response = await api.put('/admin/system-settings/browser-extension', {
      setting_value: enabled,
      enabled: enabled
    });
    return response.data;
  },

  // Get Reconciliation Issues page status
  getReconciliationIssuesStatus: async () => {
    const api = createAuthenticatedRequest();
    const response = await api.get('/admin/system-settings/reconciliation-issues');
    return response.data;
  },

  // Update Reconciliation Issues page status
  updateReconciliationIssuesStatus: async (enabled) => {
    const api = createAuthenticatedRequest();
    const response = await api.put('/admin/system-settings/reconciliation-issues', {
      setting_value: enabled,
      enabled: enabled
    });
    return response.data;
  },

  // Get Create AWB page status
  getAWBCreateStatus: async () => {
    const api = createAuthenticatedRequest();
    const response = await api.get('/admin/system-settings/awb-create');
    return response.data;
  },

  // Update Create AWB page status
  updateAWBCreateStatus: async (enabled) => {
    const api = createAuthenticatedRequest();
    const response = await api.put('/admin/system-settings/awb-create', {
      setting_value: enabled,
      enabled: enabled
    });
    return response.data;
  },

  // Get system updates status
  getSystemUpdates: async () => {
    const api = createAuthenticatedRequest();
    const response = await api.get('/admin/system-updates');
    return response.data;
  }
};

export default adminMonitoringAPI;
